import { ManageableItemsList, Tooltip } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useState, type JSX } from 'react';

import useGetStockLocationStockLevels from '@/api/queries/stockLocations/useGetStockLocationStockLevels';
import { StockLocationStockLevelsQuery } from '@/gql/graphql';
import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';

import FractionalCurrencyValue from './FractionalCurrencyValue';

type StockLevel = NonNullable<
  StockLocationStockLevelsQuery['stockLocation']
>['materialsList']['stockLevels']['items'][number];

type Props = {
  stockLocationId: string;
};

const PAGE_SIZE = 50;

const StockLocationStockLevels: FC<Props> = (props: Props): JSX.Element => {
  const { stockLocationId } = props;

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const numberFormat = usePreferredNumberFormat();

  const { stockLevels, isLoading } = useGetStockLocationStockLevels(
    stockLocationId,
    { searchTerm: appliedSearchTerm || undefined },
    { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }
  );

  return (
    <ManageableItemsList>
      <ManageableItemsList.ActionBar>
        <ManageableItemsList.ActionBar.SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          onChangeTimeout={(value) => {
            setPage(1);
            setAppliedSearchTerm(value);
          }}
          changeTimeoutMs={250}
        />
      </ManageableItemsList.ActionBar>
      <ManageableItemsList.Table
        isLoading={isLoading}
        items={stockLevels?.items ?? []}
        uniqueKey="id"
        emptyText="No materials in stock at this location yet."
      >
        <ManageableItemsList.Table.Column<StockLevel, 'material'>
          header="Material"
          field="material"
          render={(material) => material.name}
        />
        <ManageableItemsList.Table.Column<StockLevel, 'onHandQuantity'>
          header="In stock"
          field="onHandQuantity"
          className="text-right"
          render={(onHandQuantity) => numberFormat.format(onHandQuantity)}
        />
        <ManageableItemsList.Table.Column<
          StockLevel,
          'weightedAverageCostExcTax'
        >
          header={
            <Tooltip tooltipContent="Weighted average cost - the average ex-tax cost of one unit on hand, across all stock held at this location.">
              <span className="inline-flex items-center gap-1">
                WAC
                <UntitledIcon name="info-circle.3" className="h-3.5 w-3.5" />
              </span>
            </Tooltip>
          }
          field="weightedAverageCostExcTax"
          className="min-w-36 text-right"
          render={(unitCost) => <FractionalCurrencyValue value={unitCost} />}
        />
        <ManageableItemsList.Table.Column<StockLevel, 'totalValueExcTax'>
          header="Total value (ex tax)"
          field="totalValueExcTax"
          className="min-w-36 text-right"
          render={(totalValue) => (
            <FractionalCurrencyValue value={totalValue} />
          )}
        />
      </ManageableItemsList.Table>
      <ManageableItemsList.PaginationBar
        type="custom"
        pageSize={PAGE_SIZE}
        currentPage={page}
        totalItems={stockLevels?.totalCount ?? 0}
        onPageChange={setPage}
      />
    </ManageableItemsList>
  );
};

export default StockLocationStockLevels;
